/**
 * Wrapper around PanelArtifact to display the detailed view of
 * registered artifact collections
 */
import WandbLoader from '@wandb/weave/common/components/WandbLoader';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {useConfig} from '@wandb/weave/components/Panel2/panel';
import {
  constNone,
  constString,
  opProjectArtifact,
  opRootProject,
} from '@wandb/weave/core';
import {useNodeValue} from '@wandb/weave/react';
import * as React from 'react';
import {useCallback, useContext, useEffect, useMemo} from 'react';
import {Link, useHistory} from 'react-router-dom';

import {envIsPublicCloud} from '../../../config';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {setDocumentTitle} from '../../../util/document';
import * as urls from '../../../util/urls';
import {WeaveErrorBoundary} from '../../Panel2/ErrorBoundary';
import {
  REGISTRY_PROJECT_PREFIX,
  resolveRegistryUrl,
} from '../../Registries/common/utils';
import {ModelRegistryMigrationContextProvider} from '../../Registries/ModelRegistryMigration/ModelRegistryMigrationContextProvider';
import {EntryPoint} from '../../Registries/ModelRegistryMigration/utils';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import PanelArtifact, {
  ConfigType as PanelArtifactConfigType,
} from './PanelArtifact/component';
import * as S from './PanelArtifactDetailView.styles';

export type ConfigType = {
  collectionPath?: {
    entityName: string;
    projectName: string;
    collectionName: string;
  };
  searchFilter?: string;
  selectedArtifactConfig?: PanelArtifactConfigType;
};
type PanelArtifactRegistryProps = {
  registryName: string;
};

export const PanelArtifactDetailView = ({
  registryName,
}: PanelArtifactRegistryProps) => {
  /*
    The input is the registry name.
    We set up the config, a dict which holds the currently selected collection which we will render in the UI.
    The currently selected portfolio is defined by a full path: {entityName}, {projectName}, {collectionName}
    and this path is determined from the URL.
    A properly formed registry URL ends with /registry/<registryName>?selectionPath={entityName}%2F{projectName}%2F{collectionName}.
   */

  const history = useHistory();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const accountOrgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const {
    selectionPath,
    selectedCollectionView,
    selectedMembershipIdentifier,
    selectedMembershipTab,
  } = urls.parseEntityRegistrySearch(window.location.search);

  const entityName = useMemo(() => selectionPath?.entityName, [selectionPath]);
  const projectName = useMemo(
    () => selectionPath?.projectName,
    [selectionPath]
  );
  const collectionName = useMemo(
    () => selectionPath?.collectionName,
    [selectionPath]
  );
  const currentArtifactNode = useMemo(() => {
    if (entityName == null || projectName == null || collectionName == null) {
      return constNone();
    }
    return opProjectArtifact({
      project: opRootProject({
        entityName: constString(entityName),
        projectName: constString(projectName),
      }),
      artifactName: constString(collectionName),
    });
  }, [collectionName, entityName, projectName]);

  const currentArtifact = useNodeValue(currentArtifactNode);

  const [artifactRegistryConfig, updateArtifactRegistryConfig] =
    useConfig<ConfigType>({
      collectionPath: selectionPath,
      selectedArtifactConfig: {
        selectedCollectionView: selectedCollectionView as any,
        tabConfigs: {
          overview: {
            selectedTab: selectedMembershipTab as any,
          },
        },
        // The following field is only applicable when selectedCollectionView is 'membership'
        // selectedMembershipIdentifier determines the membership identifier selected - null should default to the latest membership version
        selectedMembershipIdentifier,
      },
      searchFilter: undefined,
    });

  // Force an update of the config when the url params change
  useEffect(() => {
    let newSelectionPath = undefined;
    if (
      selectionPath?.entityName &&
      selectionPath?.projectName &&
      selectionPath?.collectionName
    ) {
      newSelectionPath = {
        entityName: selectionPath.entityName,
        projectName: selectionPath.projectName,
        collectionName: selectionPath.collectionName,
      };
    }
    updateArtifactRegistryConfig({
      collectionPath: newSelectionPath,
      selectedArtifactConfig: {
        selectedCollectionView: selectedCollectionView as any,
        tabConfigs: {
          overview: {
            selectedTab: selectedMembershipTab as any,
          },
        },
        selectedMembershipIdentifier,
      },
    });
  }, [
    selectionPath?.entityName,
    selectionPath?.projectName,
    selectionPath?.collectionName,
    selectedCollectionView,
    selectedMembershipIdentifier,
    selectedMembershipTab,
    updateArtifactRegistryConfig,
  ]);

  const updateConfig = useCallback(
    partialUpdate => {
      const currentURL = window.location.pathname + window.location.search;
      const newConfig = {...artifactRegistryConfig, ...partialUpdate};
      const isRegistryProject =
        newConfig.collectionPath?.projectName.startsWith(
          REGISTRY_PROJECT_PREFIX
        );
      const newURL = newConfig.collectionPath
        ? resolveRegistryUrl(
            isRegistryProject,
            accountOrgName,
            registryName,
            {
              entityName: newConfig.collectionPath?.entityName,
              projectName: newConfig.collectionPath?.projectName,
              collectionName: newConfig.collectionPath?.collectionName,
            },
            selectionPath?.entityName,
            newConfig.selectedArtifactConfig?.selectedCollectionView,
            newConfig.selectedArtifactConfig?.selectedMembershipIdentifier,
            newConfig.selectedArtifactConfig?.tabConfigs?.overview?.selectedTab
          )
        : resolveRegistryUrl(
            projectName?.startsWith(REGISTRY_PROJECT_PREFIX) ?? false,
            accountOrgName,
            registryName,
            undefined,
            entityName
          );

      if (newURL !== currentURL) {
        history.replace(newURL);
      }

      updateArtifactRegistryConfig(partialUpdate);
    },
    [
      artifactRegistryConfig,
      history,
      registryName,
      entityName,
      projectName,
      selectionPath?.entityName,
      accountOrgName,
      updateArtifactRegistryConfig,
    ]
  );

  // This handles the URLs: /registry/model and /registry/model/?selectionPath={invalid path}
  // Upon {invalid path} we route to the registry homepage
  useEffect(() => {
    if (currentArtifact.loading) {
      return;
    }
    if (currentArtifact.result == null) {
      updateConfig({
        collectionPath: undefined,
      });
    } else if (selectionPath) {
      setDocumentTitle(selectionPath.collectionName);
    }
  }, [selectionPath, currentArtifact, updateConfig]);

  const artifactConfig = Panel2.useConfigChild(
    'selectedArtifactConfig',
    artifactRegistryConfig,
    updateConfig
  );

  if (currentArtifact.loading) {
    return <WandbLoader name="artifact-collection" />;
  }

  const mrMigrationContextEntrypoint = selectionPath?.projectName?.startsWith(
    REGISTRY_PROJECT_PREFIX
  )
    ? EntryPoint.GLOBAL_REGISTRY
    : EntryPoint.LEGACY_MR;
  return (
    <ModelRegistryMigrationContextProvider
      entryPoint={mrMigrationContextEntrypoint}
      mrEntityName={selectionPath?.entityName}
      artifactCollectionName={selectionPath?.collectionName}>
      <S.GeneralPageContainer>
        <S.RegistryContents>
          <S.MinimizedSidebar>
            <Link
              to={resolveRegistryUrl(
                selectionPath?.projectName?.startsWith(
                  REGISTRY_PROJECT_PREFIX
                ) ?? false,
                accountOrgName,
                registryName,
                undefined,
                selectionPath?.entityName
              )}>
              <Button
                icon="back"
                variant="ghost"
                tooltip={`Back to ${registryName} registry`}
              />
            </Link>
          </S.MinimizedSidebar>
          {artifactConfig != null && selectionPath != null && (
            <S.CollectionDetailViewContainer>
              <S.CollectionDetailView>
                <WeaveErrorBoundary
                  canReset={false}
                  canUndo={false}
                  errorConfig={artifactConfig.config}
                  sourcePanel={'PanelArtifact'}
                  onReset={() => {}}
                  onUndo={() => {}}>
                  <PanelArtifact
                    {...Panel2.dummyProps}
                    config={artifactConfig.config}
                    updateConfig={artifactConfig.updateConfig}
                    input={currentArtifactNode as any}
                    artifactRegistry={true}
                  />
                </WeaveErrorBoundary>
              </S.CollectionDetailView>
            </S.CollectionDetailViewContainer>
          )}
        </S.RegistryContents>
      </S.GeneralPageContainer>
    </ModelRegistryMigrationContextProvider>
  );
};
